






































































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import {FileFormat} from '@/enums/FileFormat'

const ACCEPTED_FORMATS = [
  FileFormat.JPG,
  FileFormat.JPEG,
  FileFormat.PNG,
  FileFormat.GIF,
]

// Mirrors the caps in org.ndapp.client.request.ContactRequest, which is where
// they are actually enforced — these only spare the user a rejected upload.
const MAX_FILES = 5
const MAX_FILE_BYTES = 5 * 1024 * 1024
const MAX_TOTAL_BYTES = 10 * 1024 * 1024

interface AttachmentPreview {
  file: File
  url: string
}

@Component
export default class ContactAttachments extends Vue {
  @Prop({type: Array, default: () => []}) value!: File[]

  isDragging = false

  /** Object URLs mirroring `value`, kept here so they can be revoked on removal. */
  previews: AttachmentPreview[] = []

  activePreview: AttachmentPreview | null = null
  isPreviewOpen = false

  get acceptAttribute() {
    return ACCEPTED_FORMATS.map(format => `.${format}`).join(',')
  }

  get readableFormats() {
    return ACCEPTED_FORMATS.map(format => format.toUpperCase()).join(', ')
  }

  get maxFiles() {
    return MAX_FILES
  }

  get maxFileMb() {
    return MAX_FILE_BYTES / (1024 * 1024)
  }

  @Watch('value', {immediate: true})
  syncPreviews(files: File[]) {
    const next = files.map(
      file =>
        this.previews.find(preview => preview.file === file) ?? {
          file,
          url: URL.createObjectURL(file),
        }
    )

    this.previews
      .filter(preview => !next.includes(preview))
      .forEach(preview => URL.revokeObjectURL(preview.url))

    this.previews = next

    // The open image may have just been removed, and its URL is already revoked.
    if (this.activePreview && !next.includes(this.activePreview)) {
      this.closePreview()
    }
  }

  openPreview(preview: AttachmentPreview) {
    this.activePreview = preview
    this.isPreviewOpen = true
  }

  closePreview() {
    this.isPreviewOpen = false
    this.activePreview = null
  }

  beforeDestroy() {
    this.previews.forEach(preview => URL.revokeObjectURL(preview.url))
  }

  openFilePicker() {
    const fileInput = this.$refs.fileInput as HTMLInputElement
    fileInput.click()
  }

  handleDrop(event: DragEvent) {
    this.isDragging = false
    this.addFiles(event.dataTransfer?.files ?? null)
  }

  handleSelect(event: Event) {
    const input = event.target as HTMLInputElement
    this.addFiles(input.files)
    // Allows re-picking the same file right after removing it.
    input.value = ''
  }

  addFiles(fileList: FileList | null) {
    if (!fileList?.length) {
      return
    }

    const incoming = Array.from(fileList)
    const wrongType = incoming.filter(file => !this.isAllowed(file))
    const tooBig = incoming.filter(
      file => this.isAllowed(file) && file.size > MAX_FILE_BYTES
    )

    // `error`, not `abort`: abort throws, which would drop the valid files in a
    // mixed selection and skip the second warning entirely.
    if (wrongType.length) {
      this.warn('fileTypeNotAllowed', {
        files: this.names(wrongType),
        formats: this.readableFormats,
      })
    }

    if (tooBig.length) {
      this.warn('fileTooLarge', {
        files: this.names(tooBig),
        size: this.maxFileMb,
      })
    }

    const accepted = incoming.filter(
      file => this.isAllowed(file) && file.size <= MAX_FILE_BYTES
    )

    if (!accepted.length) {
      return
    }

    if (this.value.length + accepted.length > MAX_FILES) {
      this.warn('tooManyFiles', {files: MAX_FILES})
      return
    }

    const next = [...this.value, ...accepted]

    if (next.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES) {
      this.warn('totalTooLarge', {size: MAX_TOTAL_BYTES / (1024 * 1024)})
      return
    }

    this.$emit('input', next)
  }

  private names(files: File[]) {
    return files.map(file => file.name).join(', ')
  }

  /** Translates with params up front, since the toast helper takes none. */
  private warn(key: string, params: Record<string, unknown>) {
    const message = this.$translate(
      `components.contactAttachments.${key}`,
      params
    ) as string

    this.$toast.error(message, undefined, false)
  }

  isAllowed(file: File) {
    const extension =
      file.name
        .split('.')
        .pop()
        ?.toLowerCase() ?? ''
    return ACCEPTED_FORMATS.includes(extension as FileFormat)
  }

  remove(index: number) {
    this.$emit(
      'input',
      this.value.filter((_, i) => i !== index)
    )
  }
}
